import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatDate, calculateAge, debounce } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Search,
  Plus,
  Filter,
  Users,
  ChevronRight,
  Phone,
  Mail,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

export default function PatientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isAuthenticated } = useAuth();
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    gender: searchParams.get('gender') || '',
    blood_type: searchParams.get('blood_type') || '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Abort controller for cancelling requests on unmount/navigation
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const fetchPatients = useCallback(async (search = '', filterParams = {}, retry = 0) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError(null);
    
    try {
      const params = { search, ...filterParams };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      
      console.log('[PatientsPage] Fetching patients with params:', params);
      
      const response = await patientsAPI.getAll(params);
      
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        console.log('[PatientsPage] Fetched', response.data?.length || 0, 'patients');
        setPatients(response.data || []);
        setError(null);
        setRetryCount(0);
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        console.log('[PatientsPage] Request cancelled');
        return;
      }
      
      // Only handle error if component is mounted
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch patients';
        console.error('[PatientsPage] Error fetching patients:', {
          status: err.response?.status,
          message: errorMessage,
          fullError: err
        });
        
        setError(errorMessage);
        
        // Retry with exponential backoff (max 3 retries)
        if (retry < 3 && err.response?.status !== 401 && err.response?.status !== 403) {
          const delay = Math.pow(2, retry) * 1000; // 1s, 2s, 4s
          console.log(`[PatientsPage] Retrying in ${delay}ms (attempt ${retry + 1}/3)`);
          setRetryCount(retry + 1);
          
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchPatients(search, filterParams, retry + 1);
            }
          }, delay);
        } else {
          toast.error(errorMessage);
        }
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Initial fetch - wait for auth to be ready
  useEffect(() => {
    // Only fetch if user is authenticated and auth loading is complete
    if (isAuthenticated) {
      console.log('[PatientsPage] Auth ready, fetching patients...');
      fetchPatients(searchQuery, filters);
    } else {
      console.log('[PatientsPage] Waiting for authentication...', { isAuthenticated });
    }
  }, [isAuthenticated, fetchPatients]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchParams(prev => {
        if (query) prev.set('search', query);
        else prev.delete('search');
        return prev;
      });
      fetchPatients(query, filters);
    }, 300),
    [filters, fetchPatients]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setSearchParams(prev => {
      if (value) prev.set(key, value);
      else prev.delete(key);
      return prev;
    });
    fetchPatients(searchQuery, newFilters);
  };

  const clearFilters = () => {
    setFilters({ gender: '', blood_type: '' });
    setSearchParams(prev => {
      prev.delete('gender');
      prev.delete('blood_type');
      return prev;
    });
    fetchPatients(searchQuery, {});
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchPatients(searchQuery, filters);
  };

  const hasActiveFilters = filters.gender || filters.blood_type;

  return (
    <div className="space-y-6 animate-fadeIn p-2" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary mb-1">Patient Management</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Patients</h1>
          <p className="text-slate-500 mt-1">
            Manage and view patient records
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRetry}
            disabled={loading}
            title="Refresh"
            className="rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
          {hasPermission('patients:create') && (
            <Button 
              onClick={() => navigate('/app/patients/new')} 
              className="gap-2 rounded-full btn-primary-glow" 
              data-testid="add-patient-btn"
            >
              <Plus className="h-4 w-4" />
              Add Patient
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="premium-card p-5">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, ID, email, or phone..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-11 h-12 rounded-xl bg-white border-slate-200 focus:border-primary/50 focus:ring-primary/10"
              data-testid="patient-search"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex gap-2">
            <Button
              variant={showFilters ? 'secondary' : 'ghost'}
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "gap-2 rounded-full px-5",
                showFilters ? "bg-primary/10 text-primary" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200"
              )}
              data-testid="filter-toggle"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <Badge className="ml-1 h-5 w-5 p-0 justify-center bg-primary text-white rounded-full text-xs">
                  {Object.values(filters).filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap gap-5 items-end animate-fadeIn">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Gender</label>
              <Select 
                value={filters.gender || "all"} 
                onValueChange={(v) => handleFilterChange('gender', v === "all" ? "" : v)}
              >
                <SelectTrigger className="w-[140px] rounded-xl bg-white border-slate-200" data-testid="gender-filter">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Blood Type</label>
              <Select 
                value={filters.blood_type || "all"} 
                onValueChange={(v) => handleFilterChange('blood_type', v === "all" ? "" : v)}
              >
                <SelectTrigger className="w-[140px] rounded-xl bg-white border-slate-200" data-testid="blood-type-filter">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="A+">A+</SelectItem>
                  <SelectItem value="A-">A-</SelectItem>
                  <SelectItem value="B+">B+</SelectItem>
                  <SelectItem value="B-">B-</SelectItem>
                  <SelectItem value="AB+">AB+</SelectItem>
                  <SelectItem value="AB-">AB-</SelectItem>
                  <SelectItem value="O+">O+</SelectItem>
                  <SelectItem value="O-">O-</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters} 
                className="gap-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full"
              >
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="premium-card p-5 border-red-200 bg-red-50">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-red-600">Failed to load patients</p>
              <p className="text-sm text-red-500">{error}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRetry}
              className="rounded-full bg-white hover:bg-red-50 border border-red-200"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="premium-card overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                  {retryCount > 0 && <span className="text-sm text-slate-500">(retry {retryCount}/3)</span>}
                </span>
              ) : (
                `${patients.length} Patient${patients.length !== 1 ? 's' : ''}`
              )}
            </h2>
          </div>
        </div>
        <div className="p-0">
          {loading && !error ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4">
                <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
                <p className="text-slate-500 text-sm">Loading patients...</p>
              </div>
            </div>
          ) : patients.length === 0 && !error ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No patients found</h3>
              <p className="text-slate-500 mb-6">
                {searchQuery || hasActiveFilters
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first patient'}
              </p>
              {hasPermission('patients:create') && !searchQuery && !hasActiveFilters && (
                <Button onClick={() => navigate('/app/patients/new')} className="rounded-full btn-primary-glow gap-2">
                  <Plus className="h-4 w-4" />
                  Add Patient
                </Button>
              )}
            </div>
          ) : !error && (
            <ScrollArea className="h-[500px]">
              <table className="premium-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Patient</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">ID</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden sm:table-cell">Age / DOB</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Contact</th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Blood Type</th>
                    <th className="px-5 py-4 w-[50px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr
                      key={patient.id}
                      className="border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => navigate(`/app/patients/${patient.id}`)}
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/10 flex-shrink-0">
                            <span className="font-semibold text-sm text-primary">
                              {patient.first_name?.[0]}{patient.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {patient.first_name} {patient.last_name}
                            </p>
                            <p className="text-sm text-slate-500 capitalize">
                              {patient.gender}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        <code className="text-xs bg-slate-100 px-2.5 py-1 rounded-lg font-mono text-slate-600">
                          {patient.patient_id}
                        </code>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <div>
                          <p className="font-medium text-slate-900">{calculateAge(patient.date_of_birth)} yrs</p>
                          <p className="text-xs text-slate-500">
                            {formatDate(patient.date_of_birth)}
                          </p>
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell">
                        <div className="space-y-1">
                          {patient.phone && (
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                              <Phone className="h-3 w-3 text-slate-400" />
                              {patient.phone}
                            </div>
                          )}
                          {patient.email && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Mail className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">{patient.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell">
                        {patient.blood_type ? (
                          <Badge className="bg-red-50 text-red-600 border border-red-200 rounded-lg">
                            {patient.blood_type}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  );
}
